import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { radiologyReportService, radiologyOrderService } from '../../lib/services.radiology';
import { ExternalLink, Download } from 'lucide-react';
import toast from 'react-hot-toast';

export default function RadiologyReportViewer() {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReport();
  }, [id]);

  const loadReport = async () => {
    if (!id) return;

    try {
      const reportData = await radiologyReportService.getById(id);
      setReport(reportData);

      if (reportData?.order_id) {
        const orderData = await radiologyOrderService.getById(reportData.order_id);
        setOrder(orderData);
      }
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Report not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Radiology Report</h1>
        <p className="text-gray-600">Report No: {report.report_no}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 max-w-4xl space-y-6">
        <div className="grid grid-cols-2 gap-4 pb-4 border-b">
          <div>
            <div className="text-sm text-gray-600">Patient</div>
            <div className="font-medium">{order?.patient?.name || 'Unknown'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Order No</div>
            <div className="font-medium">{order?.order_no}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Modality</div>
            <div className="font-medium">{report.modality || 'N/A'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Finalized At</div>
            <div className="font-medium">
              {report.finalized_at ? new Date(report.finalized_at).toLocaleString() : 'N/A'}
            </div>
          </div>
        </div>

        {report.findings && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Findings</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{report.findings}</p>
          </div>
        )}

        {report.impression && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Impression</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{report.impression}</p>
          </div>
        )}

        {report.recommendations && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Recommendations</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{report.recommendations}</p>
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t">
          {report.pdf_url && (
            <button
              onClick={async () => {
                try {
                  const url = await radiologyReportService.getSignedUrl(report.pdf_url);
                  window.open(url, '_blank');
                } catch (error) {
                  toast.error('Failed to open PDF');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
